import styled, { css, keyframes } from 'styled-components';

import { Text } from '../../components/Text/Text';
import { COLORS, ANIM, MOBILE, REDUCED_MOTION } from '../../theme/tokens';

/* Something just added: its outline lights up in the CTA colour and fades back to the hairline. The list editor
   plays it on a game just added to a list. */
export const addedFlash = keyframes`
  0% {
    border-color: ${COLORS.ctaBorder};
    box-shadow: 0 0 0 2px ${COLORS.ctaSolid};
  }
  100% {
    border-color: ${COLORS.hairline};
    box-shadow: 0 0 0 2px transparent;
  }
`;

/** How long that flash lasts; a component clears its mark on the same beat. */
export const ADDED_FLASH_MS = 900;

export const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const Subtitle = styled(Text).attrs({ tag: 'span' as const })`
  display: block;
  color: ${COLORS.textMuted};
  font-size: 14px;
  max-width: 56ch;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
`;

/* Tabs bar. */
/* Secondary tab row: the SURFACE tabs (Homepage / Promotions / …) inside the Banners view. Underline
   style (square bottom, a 2px orange border marks the active tab) — distinct from the primary pill
   tabs. Data-driven, so new surfaces just add a tab. */
export const SurfaceTabs = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 22px;
  margin: 4px 0 8px;
  border-bottom: 1px solid ${COLORS.hairline};

  ${MOBILE} {
    /* The surface row can hold several tabs; on the phone it becomes a horizontally-scrollable strip
       (no wrap, hidden scrollbar) that CONTAINS its own overflow, so long labels no longer widen the
       page. A soft right-edge fade signals there's more to scroll to; the trailing padding gives the
       LAST tab room to scroll clear of that fade (without it, the final tab sits permanently dimmed
       flush against the edge and reads as unreachable). */
    gap: 20px;
    min-width: 0;
    max-width: 100%;
    overflow-x: auto;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding-right: 32px;
    scroll-padding-inline-end: 32px;
    &::-webkit-scrollbar {
      display: none;
    }
    -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 28px), transparent); /* design-allow: mask alpha */
    mask-image: linear-gradient(to right, #000 calc(100% - 28px), transparent); /* design-allow: mask alpha */
  }
`;

/* A subtle vertical divider in the sub-tab row, separating the curated carousels (Homepage / Casino /
   Sports) from the pool ("all Promotions"). */
export const TabSep = styled.span`
  align-self: center;
  width: 1px;
  height: 16px;
  margin-bottom: 4px;
  background: ${COLORS.hairline};
`;

export const SurfaceTab = styled.button<{ $active: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: transparent;
  border: 0;
  border-bottom: 2px solid ${({ $active }) => ($active ? COLORS.ctaBorder : 'transparent')};
  margin-bottom: -1px; /* sit the 2px active border over the row's 1px hairline */
  padding: 0 1px 10px;
  font-family: inherit;
  font-size: 15px;
  font-weight: ${({ $active }) => ($active ? 700 : 600)};
  letter-spacing: -0.01em;
  color: ${({ $active }) => ($active ? COLORS.heading : COLORS.textMuted)};
  cursor: pointer;
  transition:
    color 140ms ${ANIM.EASE},
    border-color 140ms ${ANIM.EASE};

  &:hover {
    color: ${COLORS.heading};
  }

  ${MOBILE} {
    /* Don't shrink or wrap in the scrollable strip — each tab keeps its natural width on one line.
       Add top padding so the tap target clears ~40px (the base only has bottom padding + the underline
       sits at the flex-end baseline, so this grows the target upward without moving the underline). */
    flex: 0 0 auto;
    white-space: nowrap;
    padding-top: 12px;
  }
`;

/* Slide-in + fade for tab-content switches (main tabs and surface tabs). Keyed on the active tab so it
   replays on every switch. */
const switchIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(10px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

export const SwitchFade = styled.div<{ $free?: boolean }>`
  /* Filled both ways: the transform it holds keeps a stacking context, which keeps what a tab layers inside it under the
     page's header scrim. \`$free\` fills only backwards: its end is the element's own look, with no context left to trap
     what inside it must stand over the page's fixed chrome (Game Lists' Create a new list over the bottom navigation's
     scrim). */
  animation: ${switchIn} ${ANIM.CREATIVE_TAB_SWITCH_MS}ms ${ANIM.EASE} ${({ $free }) => ($free ? 'backwards' : 'both')};

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/* One placement section (a group of creatives that share a slot / category). */
export const Section = styled.section<{ $bleed?: boolean }>`
  margin-top: 24px;

  &:first-of-type {
    margin-top: 18px;
  }

  /* Full-bleed (immersive Advanced Video editor): drop the top margin so the canvas starts flush at the
     top-bar line — the composer's viewport-height anchor assumes nothing sits above it. */
  ${(p) =>
    p.$bleed &&
    css`
      margin-top: 0;

      &:first-of-type {
        margin-top: 0;
      }
    `}
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

export const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const SectionName = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 15px;
  font-weight: 650;
  color: ${COLORS.heading};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Count = styled.span`
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.textMuted};
  background: ${COLORS.surfaceInset};
  padding: 2px 9px;
  border-radius: 999px;
  font-variant-numeric: tabular-nums;
`;

/* "Generating" pill on a placement whose run is in flight. */
export const StatusPill = styled.span<{ $tone: 'run' | 'ready' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  color: ${({ $tone }) => ($tone === 'ready' ? COLORS.accent : COLORS.textMuted)};
  border: 1px solid ${({ $tone }) => ($tone === 'ready' ? COLORS.accent : COLORS.hairline)};
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
`;

/* Custom tab. */
export const CustomIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 18px;
`;

export const CustomTitle = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 16px;
  font-weight: 650;
  color: ${COLORS.heading};
`;

export const CustomBody = styled(Text).attrs({ tag: 'span' as const })`
  color: ${COLORS.textMuted};
  font-size: 14px;
  max-width: 56ch;
`;

export const PlacementPicker = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
`;

export const PickTile = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
  border-radius: 14px;
  cursor: pointer;
  background: ${COLORS.surfaceFaint};
  border: 1px solid ${COLORS.hairline};
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${COLORS.hoverBorder};
  }
`;
